/* @flow */

import * as React from 'react';
import Helmet from 'react-helmet';
import numeral from 'numeral';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { findIndex, get } from 'lodash/fp';
import { getTransactions } from 'app/actions/userActions';
import useSheet from 'app/jss';
import RowHeader from 'app/components/commons/RowHeader';
import Spinner from 'app/components/commons/Spinner';
import PaginationBox from 'app/components/commons/PaginationBox';
import TransactionRow from 'app/components/user/TransactionRow';
import LightTab from '../../components/commons/LightTab';
import LightTabSet from '../../components/commons/LightTabSet';

type Props = {
  classes: Object,
  isFetching?: boolean,
  onFetch: (?string, ?number) => void,
  pagination?: Object,
  transactions: Array<*>,
  type?: string,
  url: string
};

const NoResults = styled.div(({ theme }) => ({
  fontSize: theme.fontSize.default,
  padding: 20,
  textAlign: 'center',
  textTransform: 'uppercase',
  color: theme.color.basis4
}));

const TabContainer = styled.div({
  width: '100%',
  maxWidth: 120 * 4,
  margin: '0 auto'
});

const TabWrapper = styled.div(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.color.basis6
}));

const tabs = [
  { name: 'All', link: '/account/transactions' },
  { name: 'Deposits', link: '/account/transactions/deposits' },
  { name: 'Withdrawals', link: '/account/transactions/withdrawals' },
  { name: 'Wins', link: '/account/transactions/wins' }
];

class AccountTransactions extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    isFetching: false,
    pagination: {},
    type: undefined
  };

  componentDidMount() {
    const { onFetch, type } = this.props;
    onFetch(type);
  }

  componentDidUpdate(prevProps: Props) {
    const { onFetch, type } = this.props;

    if (type !== prevProps.type) {
      onFetch(type);
    }
  }

  handlePageChange = page => {
    const { onFetch, type } = this.props;
    onFetch(type, page);
  };

  render() {
    const { classes, isFetching, pagination, transactions, url } = this.props;

    return (
      <div>
        <Helmet title="Transactions" />
        <TabWrapper>
          <TabContainer>
            <LightTabSet selectedIndex={findIndex(t => t.link === url, tabs)}>
              {tabs.map(tab => (
                <LightTab key={tab.name} selected={tab.link === url} to={tab.link}>
                  {tab.name}
                </LightTab>
              ))}
            </LightTabSet>
          </TabContainer>
        </TabWrapper>
        {pagination && (
          <PaginationBox
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onChange={this.handlePageChange}
          />
        )}
        {isFetching ? (
          <div className={classes.loader}>
            <Spinner />
          </div>
        ) : (
          <div className={classes.table}>
            <RowHeader isTableHeader first>
              <div className={`${classes.tableCell} ${classes.tableCell__Small}`}>ID</div>
              <div className={`${classes.tableCell} ${classes.tableCell__Large}`}>Date</div>
              <div className={classes.tableCellWrapper}>
                <div className={classes.tableCellNested}>
                  <div className={classes.tableCellNestedFirst}>
                    <div className={`${classes.tableCell} ${classes.tableCell__Main}`}>Name</div>
                  </div>
                  <div className={classes.tableCellNestedLast}>
                    <div className={`${classes.tableCell} ${classes.tableCell__Default}`}>
                      Amount
                    </div>
                    <div className={`${classes.tableCell} ${classes.tableCell__Small}`}>
                      Balance
                    </div>
                  </div>
                </div>
              </div>
            </RowHeader>
            {transactions.length > 0 ? (
              <div className={classes.rows}>
                {transactions.map(transaction => (
                  <TransactionRow
                    key={transaction.id}
                    amount={transaction.amount}
                    balance={numeral(transaction.balance).value()}
                    bonusAmount={transaction.bonus_amount}
                    categoryId={transaction.category.id}
                    categoryName={transaction.category.name}
                    challengeId={get('challenge.id', transaction)}
                    date={transaction.date}
                    discount={parseInt(get('coupon.discount', transaction.entry), 10) || 0}
                    entryId={get('entry.id', transaction)}
                    id={transaction.id}
                    label={
                      get('name', transaction.challenge) ||
                      get('name', transaction.payment_transaction)
                    }
                    originalPrice={get(transaction.entry, 'coupon.original_amount')}
                    refunded={transaction.refunded}
                    sheet={{ classes }}
                    type={transaction.type.name}
                    typeId={transaction.type.id}
                    paymentTransactionLabel={get('payment_transaction.label', transaction)}
                  />
                ))}
              </div>
            ) : (
              <NoResults>No entries</NoResults>
            )}
            {pagination && (
              <PaginationBox
                currentPage={pagination.current_page}
                lastPage={pagination.last_page}
                onChange={this.handlePageChange}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state, { match }) {
  return {
    isFetching: state.user.retrievingTransactions,
    pagination: state.user.transactionsPagination,
    transactions: state.user.transactions,
    type: match.params.type,
    url: match.url
  };
}

const actions = {
  onFetch(type, page) {
    return getTransactions({
      page,
      type
    });
  }
};

function applyStyles(theme: Theme) {
  return {
    iconWrapper: {
      display: 'none',
      fontSize: '0.8em',
      position: 'relative'
    },
    loader: {
      display: 'flex',
      justifyContent: 'center',
      padding: 20
    },
    transactionRow: {
      display: 'flex',
      paddingTop: theme.spacing.small,
      paddingBottom: theme.spacing.small,
      fontSize: theme.fontSize.xsmall,
      [theme.mediaQuery.desktopAndUp]: {
        fontSize: theme.fontSize.default
      }
    },
    tableCell: {
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      paddingLeft: theme.spacing.xsmall,
      paddingRight: theme.spacing.xsmall
    },
    tableCellNested: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between'
    },
    tableCellNestedFirst: {
      display: 'flex',
      flexDirection: 'row'
    },
    tableCellNestedLast: {
      display: 'flex',
      flexDirection: 'row',
      flexGrow: 1,
      justifyContent: 'flex-end'
    },
    tableCellWrapper: {
      flexGrow: 1,
      flexShrink: 1
    },
    tableCell__Small: {
      flexBasis: '50px'
      // whiteSpace: 'nowrap',
    },
    tableCell__Default: {
      flexBasis: 70
    },
    tableCell__Main: {
      flexBasis: 50,
      [theme.mediaQuery.tabletPortraitAndUp]: {
        flexGrow: 1
      }
    },
    tableCell__Large: {
      flexBasis: 70
    },
    subtext: {
      color: theme.color.basis4,
      fontSize: theme.fontSize.xxsmall,
      wordBreak: 'break-all'
    },
    pill: {
      alignSelf: 'flex-start',
      borderRadius: theme.borderRadius.small,
      color: theme.color.white,
      fontSize: theme.fontSize.xtiny,
      lineHeight: 1.1,
      paddingTop: 3,
      paddingRight: theme.spacing.small,
      paddingBottom: 3,
      paddingLeft: theme.spacing.small,
      textAlign: 'center',
      textTransform: 'uppercase'
    },
    pill_debit: {
      background: theme.color.danger
    },
    pill_credit: {
      background: theme.color.success
    },
    '@media (min-width: 360px)': {
      tableCell__Small: {
        flexBasis: 70
      },
      tableCell__Default: {
        flexBasis: 90
      }
    },
    [theme.mediaQuery.tabletPortraitAndUp]: {
      iconWrapper: {
        display: 'inline-block'
      }
    },
    [theme.mediaQuery.desktopAndUp]: {
      tableCell: {
        paddingLeft: theme.spacing.small,
        paddingRight: theme.spacing.small
      },
      tableCell__Small: {
        flexBasis: '110px'
      },
      tableCell__Default: {
        flexBasis: '120px'
      },
      tableCell__Large: {
        flexBasis: '150px'
      },
      tableCell__Main: {
        flexBasis: '180px'
      }
    }
  };
}

export default useSheet(applyStyles)(
  connect(
    mapStateToProps,
    actions
  )(AccountTransactions)
);
